import './Contact.scss';
// .Contact-submit only layers a disabled state onto .Button, which is defined
// here — imported explicitly rather than leaning on another component on the
// page having already pulled it in. Webpack dedupes the duplicate import.
import './Reusable.scss';

import { useRef, useState } from 'react';
import { getLeadSource } from '../utility/leadSource';
import { track } from '../utility/analytics';
import flame from "../resources/images/flame.svg";

// Web3Forms access keys are public by design — a routing token tied to a
// verified inbox, not a secret. Keeping it in .env means it can be rotated
// without a code change; it still ships inside the bundle either way. The
// honeypot below and Web3Forms' own filtering are what actually protect the
// free-tier quota, not hiding this string.
const ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;
const ENDPOINT = "https://api.web3forms.com/submit";

// same comma-obfuscated address the CTA buttons used to fire a mailto at —
// now the fallback for anyone the form doesn't work for
const address = "j.geig,er@can,dlely,tepro,ductio,ns.com".replaceAll(",", "");

// Mirrors the three verticals in Services, split finely enough that the inbox
// subject line alone tells you which sales motion just walked in.
const PROJECT_TYPES = [
    "Nonprofit — fundraising or campaign",
    "Nonprofit — mission or story",
    "Parish or faith-based",
    "Corporate — brand or marketing",
    "Corporate — recruitment or training",
    "Wedding",
    "Short or feature film",
    "Something else",
];

const TIMELINES = [
    "As soon as possible",
    "In the next 1–3 months",
    "In 3–6 months",
    "Just exploring for now",
];

// Bands rather than a free-text number: far lower friction to answer, and this
// is the one field that separates a real project from a browse.
const BUDGETS = [
    "Under $2,500",
    "$2,500 – $5,000",
    "$5,000 – $10,000",
    "$10,000+",
    "Not sure yet",
];

function Contact() {

    // idle | sending | success | error
    const [status, setStatus] = useState("idle");

    // the form is unmounted on success, so focus would otherwise fall back to
    // the top of the document with no indication anything happened
    const successRef = useRef(null);

    // No key configured means every submit would fail silently, so the section
    // degrades to the plain mailto rather than showing a form that goes nowhere.
    const isConfigured = Boolean(ACCESS_KEY);

    async function handleSubmit(event) {
        event.preventDefault();

        const payload = Object.fromEntries(new FormData(event.target));

        // bots fill every field they can find; a human never sees this one
        if (payload.botcheck) return;
        delete payload.botcheck;

        setStatus("sending");

        const leadSource = getLeadSource();

        try {
            const response = await fetch(ENDPOINT, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                },
                body: JSON.stringify({
                    ...payload,
                    access_key: ACCESS_KEY,
                    from_name: "Candlelyte Productions site",
                    // which CTA sent them here — set by Button, read at submit
                    leadSource,
                    subject: `New inquiry — ${payload.projectType} — ${payload.name}`,
                }),
            });

            const result = await response.json();
            setStatus(result.success ? "success" : "error");

            if (result.success) {
                // Qualification answers only. Nothing the visitor typed — no
                // name, email, organization, or message — goes to analytics.
                track('form-submitted', {
                    projectType: payload.projectType,
                    timeline: payload.timeline,
                    budget: payload.budget,
                    source: leadSource,
                });

                // after paint, once the panel it points at actually exists
                requestAnimationFrame(() => successRef.current?.focus());
            } else {
                track('form-error', { reason: 'rejected' });
            }
        } catch {
            // network failure, blocked request, offline — same recovery either way
            setStatus("error");
            track('form-error', { reason: 'network' });
        }
    }

    return (
        <div className="Contact" id="contact">

            <div className="Contact-intro reveal">
                <span className="Contact-eyebrow">Start a project</span>
                <h2>Let's talk about your story.</h2>
                <p>Tell us what you're working on. We read every one of these ourselves, and
                you'll hear back within two business days — if Candlelyte isn't the right
                fit, we'll say so and point you somewhere that is.</p>

                <ul className="Contact-assurances">
                    <li>No obligation, and no sales sequence to unsubscribe from</li>
                    <li>A rough budget range is enough — nothing has to be final</li>
                    <li>Based in Minneapolis, working across Minnesota and beyond</li>
                </ul>

                <p className="Contact-direct">
                    Prefer email? <a href={`mailto:${address}`}>{address}</a>
                </p>
            </div>

            <div className="Contact-panel reveal delay-1">
                {status === "success" ? (
                    // The form is replaced rather than hidden, and focus is moved here on
                    // success, so a keyboard or screen reader user lands on the confirmation
                    // instead of at the top of the document.
                    <div className="Contact-success" role="status" tabIndex={-1} ref={successRef}>
                        <img src={flame} alt="" aria-hidden="true" />
                        <h3>Thank you — that's in.</h3>
                        <p>Your message is on its way to our inbox. We'll get back to you
                        within two business days.</p>
                        <p className="Contact-success-note">In the meantime, if anything
                        else comes to mind, just reply straight to <a href={`mailto:${address}`}>{address}</a>.</p>
                    </div>
                ) : isConfigured ? (
                    <form className="Contact-form" onSubmit={handleSubmit}>

                        {/* honeypot — hidden from sight, from the tab order, and from
                            assistive tech, so only a script ever fills it in */}
                        <input
                            type="checkbox"
                            name="botcheck"
                            className="Contact-botcheck"
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true" />

                        <div className="Contact-field">
                            <label htmlFor="contact-name">Your name <span aria-hidden="true">*</span></label>
                            <input id="contact-name" name="name" type="text"
                                autoComplete="name" required />
                        </div>

                        <div className="Contact-field">
                            <label htmlFor="contact-email">Email <span aria-hidden="true">*</span></label>
                            <input id="contact-email" name="email" type="email"
                                autoComplete="email" required />
                        </div>

                        <div className="Contact-field">
                            <label htmlFor="contact-org">Organization <span className="optional">optional</span></label>
                            <input id="contact-org" name="organization" type="text"
                                autoComplete="organization" />
                        </div>

                        <div className="Contact-field">
                            <label htmlFor="contact-type">What kind of project? <span aria-hidden="true">*</span></label>
                            <select id="contact-type" name="projectType" required defaultValue="">
                                <option value="" disabled>Choose one</option>
                                {PROJECT_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                            </select>
                        </div>

                        <div className="Contact-field">
                            <label htmlFor="contact-timeline">Timeline <span aria-hidden="true">*</span></label>
                            <select id="contact-timeline" name="timeline" required defaultValue="">
                                <option value="" disabled>Choose one</option>
                                {TIMELINES.map(item => <option key={item} value={item}>{item}</option>)}
                            </select>
                        </div>

                        <div className="Contact-field">
                            <label htmlFor="contact-budget">Budget range <span aria-hidden="true">*</span></label>
                            <select id="contact-budget" name="budget" required defaultValue="">
                                <option value="" disabled>Choose one</option>
                                {BUDGETS.map(band => <option key={band} value={band}>{band}</option>)}
                            </select>
                        </div>

                        <div className="Contact-field full">
                            <label htmlFor="contact-message">What's the project? <span aria-hidden="true">*</span></label>
                            <textarea id="contact-message" name="message" rows={5} required
                                placeholder="Who it's for, what it needs to say, and anything already decided." />
                        </div>

                        <div className="Contact-actions full">
                            <button type="submit" className="Button Contact-submit"
                                disabled={status === "sending"}>
                                <span>{status === "sending" ? "Sending…" : "Send it over"}</span>
                                <img src={flame} alt="" aria-hidden="true" />
                            </button>

                            {/* role="alert" so the failure is announced, not just drawn */}
                            <p className="Contact-status" role="alert">
                                {status === "error" && (
                                    <>That didn't send — something went wrong on the way out.
                                    Please email us directly at{" "}
                                    <a href={`mailto:${address}`}>{address}</a> and we'll pick it up there.</>
                                )}
                            </p>
                        </div>
                    </form>
                ) : (
                    // REACT_APP_WEB3FORMS_KEY is missing from the build environment
                    <div className="Contact-success" role="status">
                        <h3>Email us directly.</h3>
                        <p>The quickest way to reach us is straight to our inbox:</p>
                        <p className="Contact-success-note">
                            <a href={`mailto:${address}`}>{address}</a>
                        </p>
                    </div>
                )}
            </div>

        </div>
    );
}

export default Contact;
